/**
 * Brand theme engine. Pure: brand inputs in, CSS custom-property maps out (see utils/theme.ts for the DOM write).
 *
 * Inputs (all optional hex strings; invalid or missing ones fall back to DEFAULT_BRAND):
 *   primary  - primary actions / CTAs               (RoofVox Vibrant Orange)
 *   accent   - AI, focus, selection, active states  (RoofVox Electric Violet)
 *   success  - success states
 *   base     - dark canvas the surface + text ramp is built from (RoofVox Brand Navy). Internal input.
 *
 * Design rules:
 *   - Surfaces come from `base`, NEVER from `primary`: a tenant's orange (or green, or blue) primary must
 *     not tint the canvas. With the default base the ramp is the exact brand neutrals.
 *   - Every text-like colour is contrast-guarded (>= 4.5:1) against the surfaces it can sit on, by the
 *     smallest lightness shift that gets there (hue kept). That is how a brand colour that is fine as a
 *     FILL (orange, violet) still yields a readable TEXT variant on light and dark surfaces.
 *   - Non-text UI (icons, focus ring) is guarded to 3:1.
 *
 * Output keys:
 *   shared - mode-independent, written as-is                         ('--ui-primary')
 *   light / dark - mode-dependent, written as `<key>-light|-dark`    ('--ui-bg' -> '--ui-bg-light')
 * index.css resolves `--ui-bg: var(--ui-bg-light, <fallback>)` in :root and the `-dark` variant in .dark,
 * which is why components need no `dark:` variants.
 */
import {
  BLACK,
  WHITE,
  channels,
  contrast,
  ensureContrast,
  hexToRgb,
  hslToRgb,
  mix,
  normalizeHex,
  rgbToHsl,
  type RGB,
} from './colorMath';
import { DEFAULT_BRAND, ROOFVOX_NEUTRALS, STATUS_BASE, WHITE_LABEL_MIN_CONTRAST } from './brandDefaults';

export interface BrandInput {
  primary?: string | null;
  accent?: string | null;
  success?: string | null;
  base?: string | null;
}

export interface BuiltTheme {
  shared: Record<string, string>;
  light: Record<string, string>;
  dark: Record<string, string>;
}

/** Ramp anchors: the eight tones every other neutral token is mixed from. */
export interface Anchors {
  bg: RGB;
  card: RGB;
  raised: RGB;
  text: RGB;
  text2: RGB;
  muted: RGB;
  steel: RGB;
}

const NAVY = hexToRgb(ROOFVOX_NEUTRALS.navy);
const STEEL = hexToRgb(ROOFVOX_NEUTRALS.steel);
const SURFACE_2 = hexToRgb(ROOFVOX_NEUTRALS.surface2);
const SURFACE_3 = hexToRgb(ROOFVOX_NEUTRALS.surface3);
const TEXT_1 = hexToRgb(ROOFVOX_NEUTRALS.text1);
const TEXT_2 = hexToRgb(ROOFVOX_NEUTRALS.text2);
const TEXT_MUTED = hexToRgb(ROOFVOX_NEUTRALS.textMuted);

/**
 * WCAG AA for body text; 3:1 for icons and focus indicators (non-text UI). The guards aim a hair above
 * the requirement so 8-bit alpha compositing in the browser (chip tints) can never land below it.
 */
const TEXT_MIN = 4.55;
const UI_MIN = 3.05;
/** Opacity a status colour is tinted over a surface for chips (`bg-success/15`). Text is guarded on it. */
const CHIP_TINT = 0.14;
/** A tenant-supplied base darker than this keeps the light-on-dark ramp meaningful. */
const MAX_BASE_LIGHTNESS = 0.14;

const sameColor = (a: RGB, b: RGB): boolean => a.every((v, i) => Math.round(v) === Math.round(b[i]));

/**
 * Round a derived colour to whole channels. Guards must run against exactly the colours that get emitted
 * (channels() rounds), otherwise a token can pass on the float value and miss by a hair once written.
 */
const snap = (c: RGB): RGB => [Math.round(c[0]), Math.round(c[1]), Math.round(c[2])];

const resolve = (value: string | null | undefined, fallback: string): RGB =>
  hexToRgb(normalizeHex(value) ?? fallback);

/** A base that is not genuinely dark is pulled down to a dark lightness (hue and saturation kept). */
export function clampBase(base: RGB): RGB {
  const [h, s, l] = rgbToHsl(base);
  return l > MAX_BASE_LIGHTNESS ? hslToRgb(h, s, MAX_BASE_LIGHTNESS) : base;
}

/**
 * Steel Blue equivalent for an arbitrary base: muted, mid-lightness, hue nudged 7 degrees toward cyan.
 * The nudge is measured from the guidelines (Brand Navy is 210 degrees, Steel Blue 203), and makes this
 * reproduce Steel Blue from Brand Navy to within two levels.
 */
export function deriveSteel(base: RGB): RGB {
  const [h, s] = rgbToHsl(base);
  return hslToRgb(h - 7, Math.min(1, s * 0.54), 0.4);
}

/**
 * Derived ramp. Calibrated on the guidelines: Surface 2 / Surface 3 sit 22% / 29% of the way from Navy to
 * Steel, and Text Muted / Secondary / Primary sit 18% / 54% / 92% of the way from Steel to white.
 * Exported so tests can prove that, fed Brand Navy, it lands within a few levels of the exact palette.
 */
export function deriveAnchors(base: RGB, steel: RGB = deriveSteel(base)): Anchors {
  return {
    bg: base,
    card: mix(base, steel, 0.22),
    raised: mix(base, steel, 0.29),
    text: mix(steel, WHITE, 0.92),
    text2: mix(steel, WHITE, 0.54),
    muted: mix(steel, WHITE, 0.18),
    steel,
  };
}

/** Exact brand neutrals for the default base, derived ramp for anything else. */
export function anchorsFor(base: RGB): Anchors {
  if (sameColor(base, NAVY)) {
    return {
      bg: NAVY,
      card: SURFACE_2,
      raised: SURFACE_3,
      text: TEXT_1,
      text2: TEXT_2,
      muted: TEXT_MUTED,
      steel: STEEL,
    };
  }
  return deriveAnchors(base);
}

export function buildTheme(input: BrandInput = {}): BuiltTheme {
  const primary = resolve(input.primary, DEFAULT_BRAND.primaryColor);
  const accent = resolve(input.accent, DEFAULT_BRAND.accentColor);
  const success = resolve(input.success, DEFAULT_BRAND.successColor);
  const base = snap(clampBase(resolve(input.base, DEFAULT_BRAND.baseColor)));
  const a = anchorsFor(base);
  const ch = channels;

  // ── Dark ramp (the brand's native mode) ────────────────────────────────────────────────────────────
  const dHover = snap(mix(a.raised, a.steel, 0.15));
  // The lightest dark surfaces: anything readable on these is readable on the (darker) page background.
  const dSurfaces: RGB[] = [a.card, a.raised, dHover].map(snap);
  // Coloured text (links, chips, status) is guarded on resting surfaces only. Hover is transient, and
  // guarding on it as well would pull every brand colour further from its hue for no real gain.
  const dToneSurfaces: RGB[] = [a.card, a.raised].map(snap);
  const dText3 = ensureContrast(a.muted, dSurfaces, TEXT_MIN, 'lighten');
  const dIcon = ensureContrast(a.steel, dSurfaces, UI_MIN, 'lighten');

  // ── Light ramp: derived from the same anchors, no new hues ─────────────────────────────────────────
  const lBg = snap(a.text);
  const lSubtle = snap(mix(lBg, a.text2, 0.15));
  const lHover = snap(mix(lBg, a.text2, 0.22));
  // The darkest light surfaces: anything readable on these is readable on white.
  const lSurfaces: RGB[] = [lBg, WHITE, lSubtle, lHover];
  const lToneSurfaces: RGB[] = [lBg, WHITE, lSubtle];
  const lText2 = snap(mix(a.bg, a.steel, 0.5));
  const lText3 = ensureContrast(a.steel, lSurfaces, TEXT_MIN, 'darken');
  const lPlaceholder = ensureContrast(a.muted, lSurfaces, TEXT_MIN, 'darken');
  const lIcon = ensureContrast(a.steel, lSurfaces, UI_MIN, 'darken');

  const shared: Record<string, string> = {};
  const light: Record<string, string> = {
    '--ui-bg': ch(lBg),
    '--ui-card': ch(WHITE),
    '--ui-raised': ch(WHITE),
    '--ui-subtle': ch(lSubtle),
    '--ui-input': ch(WHITE),
    '--ui-hover': ch(lHover),
    '--ui-border': ch(mix(WHITE, a.text2, 0.45)),
    '--ui-border2': ch(mix(WHITE, a.text2, 0.25)),
    '--ui-border3': ch(a.text2),
    '--ui-text': ch(a.bg),
    '--ui-text2': ch(lText2),
    '--ui-text3': ch(lText3),
    '--ui-textinv': ch(WHITE),
    '--ui-placeholder': ch(lPlaceholder),
    '--ui-icon': ch(lIcon),
  };
  const dark: Record<string, string> = {
    '--ui-bg': ch(a.bg),
    '--ui-card': ch(a.card),
    '--ui-raised': ch(a.raised),
    '--ui-subtle': ch(mix(a.bg, a.card, 0.5)),
    '--ui-input': ch(a.raised),
    '--ui-hover': ch(dHover),
    '--ui-border': ch(mix(a.card, a.steel, 0.25)),
    '--ui-border2': ch(mix(a.card, a.steel, 0.12)),
    '--ui-border3': ch(dIcon),
    '--ui-text': ch(a.text),
    '--ui-text2': ch(a.text2),
    '--ui-text3': ch(dText3),
    '--ui-textinv': ch(a.bg),
    '--ui-placeholder': ch(dText3),
    '--ui-icon': ch(dIcon),
  };

  /** Text-like colour guarded on the surfaces AND on a status-chip tint of itself over them. */
  const chipText = (color: RGB, surfaces: RGB[], direction: 'lighten' | 'darken'): RGB =>
    ensureContrast(
      color,
      [...surfaces, ...surfaces.map((s) => mix(s, color, CHIP_TINT))],
      TEXT_MIN,
      direction,
    );

  // ── Primary (CTAs) ─────────────────────────────────────────────────────────────────────────────────
  // The brand specifies a white label on Vibrant Orange (2.77:1). Lighter tenant primaries fall back to
  // the base navy so their buttons never become unreadable.
  shared['--ui-primary'] = ch(primary);
  shared['--ui-primary-hover'] = ch(mix(primary, BLACK, 0.12));
  shared['--ui-on-primary'] = ch(contrast(WHITE, primary) >= WHITE_LABEL_MIN_CONTRAST ? WHITE : a.bg);
  light['--ui-primary-text'] = ch(chipText(primary, lToneSurfaces, 'darken'));
  dark['--ui-primary-text'] = ch(chipText(primary, dToneSurfaces, 'lighten'));
  light['--ui-primary-soft'] = ch(mix(WHITE, primary, 0.08));
  dark['--ui-primary-soft'] = ch(mix(a.card, primary, 0.14));

  // ── Accent (AI, focus, selection) ──────────────────────────────────────────────────────────────────
  shared['--ui-accent'] = ch(accent);
  // Fill that carries a white label at AA (violet #8B62F0 -> #8459EF).
  shared['--ui-accent-fill'] = ch(ensureContrast(accent, WHITE, TEXT_MIN, 'darken'));
  shared['--ui-on-accent'] = ch(WHITE);
  light['--ui-accent-text'] = ch(chipText(accent, lToneSurfaces, 'darken'));
  dark['--ui-accent-text'] = ch(chipText(accent, dToneSurfaces, 'lighten'));
  light['--ui-accent-soft'] = ch(mix(WHITE, accent, 0.12));
  dark['--ui-accent-soft'] = ch(mix(a.card, accent, 0.16));
  // Focus ring: non-text UI, so 3:1 on whichever surfaces it lands on.
  light['--ui-ring'] = ch(ensureContrast(accent, lSurfaces, UI_MIN, 'darken'));
  dark['--ui-ring'] = ch(ensureContrast(accent, dSurfaces, UI_MIN, 'lighten'));

  // ── Status tones ───────────────────────────────────────────────────────────────────────────────────
  const tones: Record<'success' | 'warning' | 'danger', RGB> = {
    success,
    warning: hexToRgb(STATUS_BASE.warning),
    danger: hexToRgb(STATUS_BASE.danger),
  };
  (Object.keys(tones) as Array<keyof typeof tones>).forEach((name) => {
    const tone = tones[name];
    shared[`--color-${name}`] = ch(tone);
    shared[`--ui-${name}-fill`] = ch(ensureContrast(tone, WHITE, TEXT_MIN, 'darken'));
    light[`--ui-${name}-text`] = ch(chipText(tone, lToneSurfaces, 'darken'));
    dark[`--ui-${name}-text`] = ch(chipText(tone, dToneSurfaces, 'lighten'));
  });

  // ── Always-dark branded chrome (app sidebar, auth panels) ─────────────────────────────────────────
  // These surfaces stay dark in BOTH modes, so their tokens do not follow light/dark. The gradient runs
  // base -> accent, the same pairing the mobile app uses for authenticated screens (its useBrandGradient
  // returns [accent, base]), so nothing here is hardcoded and it is never derived from the primary.
  // It stops 42% of the way, as web's old Navy -> Steel ramp did, so white chrome text keeps its contrast:
  // at full strength the default violet accent would drop --ui-chrome-text to 3.7:1, under the TEXT_MIN
  // this engine guarantees everywhere else.
  const chromeFrom = a.bg;
  const chromeTo = snap(mix(a.bg, accent, 0.42));
  const chromeSurfaces: RGB[] = [chromeFrom, chromeTo];
  shared['--ui-chrome-from'] = ch(chromeFrom);
  shared['--ui-chrome-to'] = ch(chromeTo);
  shared['--ui-chrome-text'] = ch(a.text);
  shared['--ui-chrome-text2'] = ch(ensureContrast(a.text2, chromeSurfaces, TEXT_MIN, 'lighten'));
  shared['--ui-chrome-accent'] = ch(chipText(accent, chromeSurfaces, 'lighten'));
  shared['--ui-chrome-primary'] = ch(chipText(primary, chromeSurfaces, 'lighten'));
  shared['--ui-chrome-icon'] = ch(ensureContrast(a.steel, chromeSurfaces, UI_MIN, 'lighten'));

  // ── Steel (neutral chips such as "Draft", secondary chrome) ────────────────────────────────────────
  shared['--ui-steel'] = ch(a.steel);
  light['--ui-steel-text'] = ch(chipText(a.steel, lToneSurfaces, 'darken'));
  dark['--ui-steel-text'] = ch(chipText(a.steel, dToneSurfaces, 'lighten'));

  return { shared, light, dark };
}
